import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SideMenu from '../components/SideMenu';
import CookieConsent from '../components/CookieConsent';
import StickyBreadcrumb from '../components/StickyBreadcrumb';
import PrivacyPolicy from '../components/PrivacyPolicy';
import AvaliacoesGoogle from '../components/AvaliacoesGoogle';
import GoogleReview from '../components/GoogleReview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://fernandotech.com.br/avaliacoes",
      "url": "https://fernandotech.com.br/avaliacoes",
      "name": "Avaliações — Fernando Tech Ribeirão Preto",
      "description": "Avaliações reais de clientes da Fernando Tech no Google. Suporte de TI, assistência técnica e sistemas de gestão em Ribeirão Preto e região.",
      "inLanguage": "pt-BR"
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fernandotech.com.br/" },
        { "@type": "ListItem", "position": 2, "name": "Avaliações", "item": "https://fernandotech.com.br/avaliacoes" }
      ]
    }
  ]
};

export default function AvaliacoesPage() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    document.title = 'Avaliações de Clientes | Fernando Tech — Ribeirão Preto';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Veja as avaliações reais dos clientes da Fernando Tech no Google. Nota 5,0 em suporte de TI, assistência técnica e sistemas de gestão em Ribeirão Preto e região.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://fernandotech.com.br/avaliacoes';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-avaliacoes';
    script.text = JSON.stringify(PAGE_SCHEMA);
    document.head.appendChild(script);

    fetch(`${API_URL}/api/google-reviews`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setSummary(d); })
      .catch(() => {});

    return () => {
      document.title = 'Fernando Tech – Suporte Técnico Premium - Ribeirão Preto e Região';
      const ld = document.getElementById('ld-avaliacoes');
      if (ld) ld.remove();
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.href = 'https://fernandotech.com.br/';
    };
  }, []);

  return (
    <div className="bg-slate-950 min-h-screen">
      <Navbar />

      <StickyBreadcrumb>
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 hover:text-cyan-400 transition-colors">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
          <span className="flex items-center gap-1 text-cyan-400">
            <Star className="w-3.5 h-3.5" /> Avaliações
          </span>
        </nav>
      </StickyBreadcrumb>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-400 text-sm font-semibold mb-5">
            <Star className="w-3.5 h-3.5 fill-yellow-400" /> Avaliações Verificadas do Google
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
            O que nossos clientes<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              dizem sobre nós
            </span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Avaliações reais de clientes atendidos em Ribeirão Preto e região — suporte de TI,
            assistência técnica, câmeras e sistemas de gestão.
          </p>
          {summary?.rating && (
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-5xl font-extrabold text-yellow-400 tabular-nums" data-testid="reviews-rating-summary">{Number(summary.rating).toFixed(1)}</div>
                <div className="flex items-center justify-center gap-0.5 mt-1">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
              </div>
              <div className="h-14 w-px bg-slate-700" />
              <div className="text-center">
                <div className="text-5xl font-extrabold text-white tabular-nums">{summary.review_count}</div>
                <div className="text-slate-500 text-sm mt-2">avaliações no Google</div>
              </div>
            </div>
          )}
        </div>
      </section>

      <AvaliacoesGoogle showHeading={false} />
      <GoogleReview />

      <Footer onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <SideMenu />
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
}
